import { useEffect, useState } from 'react';
import {
  Briefcase,
  Building2,
  Eye,
  FileText,
  Loader2,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  useDeleteCim,
  useEmployment,
  usePersonPayroll,
  useSaveEmployment,
  useUploadCim,
} from '../../../hooks/useEmployment';
import { getCimPreviewUrl, type EmploymentType } from '../../../lib/employmentApi';
import { getPayrollDocumentUrl, type PayrollEntry } from '../../../lib/payrollApi';
import { lookupAnafByCui } from '../../../lib/anafApi';
import { ApiError } from '../../../lib/api';
import { useToast } from '../../tasks/linear/Toast';
import { useConfirm } from '../../ui/ConfirmProvider';
import { FileDropzone } from '../../onboarding/FileDropzone';
import { PayrollUploadDialog } from '../employment/PayrollUploadDialog';
import { PayrollDashboard } from '../employment/PayrollDashboard';

// ---------------------------------------------------------------------------
// "Angajare" tab on the person detail page (Echipa).
//
// Classifies the member as Contractor (B2B) — with ANAF-prefilled company data
// and IBAN — or Angajat (CIM) — with the signed CIM contract uploaded to
// storage. Below, the monthly payroll history ("ștat de plată") with the
// upload/preview/confirm dialog. Reads are gated server-side on
// people.canViewRates (the tab itself is hidden without it); writes on
// people.canEdit.
// ---------------------------------------------------------------------------

const MAX_CIM_BYTES = 10 * 1024 * 1024; // keep in sync with backend

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-500';

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const moneyFormat = new Intl.NumberFormat('ro-RO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatMoney(value: number): string {
  return moneyFormat.format(value);
}

export interface PersonEmploymentTabProps {
  personId: string;
}

export function PersonEmploymentTab({ personId }: PersonEmploymentTabProps) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const canEdit = can('people', 'canEdit');
  const toast = useToast();
  const confirm = useConfirm();

  const { data: employment, isLoading, isError } = useEmployment(personId);
  const saveEmployment = useSaveEmployment(personId);
  const uploadCim = useUploadCim(personId);
  const deleteCim = useDeleteCim(personId);

  // Local form state, hydrated from the server payload.
  const [type, setType] = useState<EmploymentType | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [companyCui, setCompanyCui] = useState('');
  const [companyRegCom, setCompanyRegCom] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [iban, setIban] = useState('');

  // ANAF lookup state (same flow as SupplierFormModal).
  const [anafLoading, setAnafLoading] = useState(false);
  const [anafError, setAnafError] = useState<string | null>(null);
  const [anafWarning, setAnafWarning] = useState<string | null>(null);
  const [anafSuccess, setAnafSuccess] = useState(false);

  // CIM preview modal (signed URL).
  const [cimPreview, setCimPreview] = useState<{ url: string; mimeType: string | null } | null>(
    null,
  );
  const [cimPreviewLoading, setCimPreviewLoading] = useState(false);

  const [payrollDialogOpen, setPayrollDialogOpen] = useState(false);

  useEffect(() => {
    setType(employment?.type ?? null);
    setCompanyName(employment?.companyName ?? '');
    setCompanyCui(employment?.companyCui ?? '');
    setCompanyRegCom(employment?.companyRegCom ?? '');
    setCompanyAddress(employment?.companyAddress ?? '');
    setIban(employment?.iban ?? '');
  }, [employment]);

  async function handleAnafLookup() {
    const digits = companyCui.trim().toUpperCase().replace(/^RO/, '');
    if (!/^\d{2,10}$/.test(digits)) {
      setAnafError(t('people.employment.anafInvalidCui'));
      return;
    }
    setAnafLoading(true);
    setAnafError(null);
    setAnafWarning(null);
    setAnafSuccess(false);
    try {
      const result = await lookupAnafByCui(digits);
      if (!result.found || !result.prefill) {
        setAnafError(t('people.employment.anafNotFound'));
        return;
      }
      setCompanyName(result.prefill.name);
      setCompanyCui(result.prefill.cifCui);
      setCompanyRegCom(result.prefill.regCom ?? '');
      setCompanyAddress(result.prefill.address);
      setAnafWarning(result.warning);
      setAnafSuccess(true);
    } catch (e) {
      setAnafError(e instanceof ApiError ? e.message : t('people.employment.anafFailed'));
    } finally {
      setAnafLoading(false);
    }
  }

  async function handleSave() {
    if (!type) return;
    try {
      await saveEmployment.mutateAsync({
        type,
        companyName: companyName.trim() || null,
        companyCui: companyCui.trim() || null,
        companyRegCom: companyRegCom.trim() || null,
        companyAddress: companyAddress.trim() || null,
        iban: iban.trim() || null,
      });
      toast.show(t('people.employment.saved'));
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : t('people.employment.saveFailed'));
    }
  }

  async function handleTypeSelect(next: EmploymentType) {
    if (!canEdit || next === type) return;
    setType(next);
    // Persist the classification immediately so the choice sticks even if the
    // admin does not fill in the details right away.
    try {
      await saveEmployment.mutateAsync({
        type: next,
        companyName: companyName.trim() || null,
        companyCui: companyCui.trim() || null,
        companyRegCom: companyRegCom.trim() || null,
        companyAddress: companyAddress.trim() || null,
        iban: iban.trim() || null,
      });
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : t('people.employment.saveFailed'));
    }
  }

  async function handleCimFile(file: File) {
    if (file.size > MAX_CIM_BYTES) {
      toast.show(t('people.employment.cimTooLarge'));
      return;
    }
    try {
      await uploadCim.mutateAsync(file);
      toast.show(t('people.employment.cimUploaded'));
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : t('people.employment.cimUploadFailed'));
    }
  }

  async function handleCimPreview() {
    setCimPreviewLoading(true);
    try {
      const res = await getCimPreviewUrl(personId);
      setCimPreview({ url: res.url, mimeType: res.mimeType });
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : t('people.employment.cimPreviewFailed'));
    } finally {
      setCimPreviewLoading(false);
    }
  }

  async function handleCimDelete() {
    if (!(await confirm({ message: t('people.employment.cimDeleteConfirm'), danger: true })))
      return;
    try {
      await deleteCim.mutateAsync();
      toast.show(t('people.employment.cimDeleted'));
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : t('people.employment.cimDeleteFailed'));
    }
  }

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('people.employment.loading')}
      </p>
    );
  }
  if (isError) {
    return <p className="text-sm text-red-600">{t('people.employment.loadError')}</p>;
  }

  return (
    <div className="space-y-6">
      {/* Type selector */}
      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-gray-900">
          {t('people.employment.typeTitle')}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <TypeCard
            icon={<Building2 className="h-5 w-5" />}
            title={t('people.employment.contractor')}
            description={t('people.employment.contractorHint')}
            selected={type === 'CONTRACTOR'}
            disabled={!canEdit}
            onSelect={() => void handleTypeSelect('CONTRACTOR')}
          />
          <TypeCard
            icon={<Briefcase className="h-5 w-5" />}
            title={t('people.employment.employee')}
            description={t('people.employment.employeeHint')}
            selected={type === 'EMPLOYEE'}
            disabled={!canEdit}
            onSelect={() => void handleTypeSelect('EMPLOYEE')}
          />
        </div>
      </section>

      {/* CONTRACTOR: company details */}
      {type === 'CONTRACTOR' && (
        <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">
            {t('people.employment.companyTitle')}
          </h2>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('people.employment.cui')}
              </label>
              <div className="flex gap-2">
                <input
                  value={companyCui}
                  onChange={(e) => {
                    setCompanyCui(e.target.value);
                    setAnafError(null);
                    setAnafSuccess(false);
                  }}
                  placeholder="RO12345678"
                  disabled={!canEdit}
                  className={inputClass}
                />
                <button
                  type="button"
                  onClick={() => void handleAnafLookup()}
                  disabled={!canEdit || anafLoading || !companyCui.trim()}
                  className="flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {anafLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  {t('people.employment.anafLookup')}
                </button>
              </div>
              {anafError && <p className="mt-1 text-xs text-red-600">{anafError}</p>}
              {anafWarning && <p className="mt-1 text-xs text-amber-600">{anafWarning}</p>}
              {anafSuccess && !anafWarning && (
                <p className="mt-1 text-xs text-green-600">{t('people.employment.anafSuccess')}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('people.employment.companyName')}
              </label>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={!canEdit}
                className={inputClass}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {t('people.employment.regCom')}
                </label>
                <input
                  value={companyRegCom}
                  onChange={(e) => setCompanyRegCom(e.target.value)}
                  disabled={!canEdit}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {t('people.employment.iban')}
                </label>
                <input
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  placeholder="RO49AAAA1B31007593840000"
                  disabled={!canEdit}
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('people.employment.address')}
              </label>
              <input
                value={companyAddress}
                onChange={(e) => setCompanyAddress(e.target.value)}
                disabled={!canEdit}
                className={inputClass}
              />
            </div>
            {canEdit && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saveEmployment.isPending}
                  className="flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {saveEmployment.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('people.employment.save')}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* EMPLOYEE: CIM contract */}
      {type === 'EMPLOYEE' && (
        <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">
            {t('people.employment.cimTitle')}
          </h2>
          {employment?.hasCim ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-5 w-5 shrink-0 text-gray-400" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-800">
                    {employment.cimFileName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatBytes(employment.cimFileSize)}
                    {employment.cimUploadedAt &&
                      ` · ${new Date(employment.cimUploadedAt).toLocaleDateString('ro-RO')}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleCimPreview()}
                  disabled={cimPreviewLoading}
                  className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  {cimPreviewLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" />
                  )}
                  {t('people.employment.cimPreview')}
                </button>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => void handleCimDelete()}
                    disabled={deleteCim.isPending}
                    className="flex items-center gap-1.5 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {t('people.employment.cimDelete')}
                  </button>
                )}
              </div>
            </div>
          ) : canEdit ? (
            uploadCim.isPending ? (
              <p className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> {t('people.employment.cimUploading')}
              </p>
            ) : (
              <FileDropzone
                accept=".pdf,application/pdf"
                hint={t('people.employment.cimHint')}
                onFile={(file) => void handleCimFile(file)}
              />
            )
          ) : (
            <p className="text-sm text-gray-400">{t('people.employment.cimEmpty')}</p>
          )}
        </section>
      )}

      {/* Financial dashboard: monthly cost chart + taxes breakdown */}
      <PayrollDashboard personId={personId} />

      {/* Payroll history */}
      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">{t('people.payroll.title')}</h2>
          {canEdit && (
            <button
              type="button"
              onClick={() => setPayrollDialogOpen(true)}
              className="flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Upload className="h-4 w-4" /> {t('people.payroll.upload')}
            </button>
          )}
        </div>
        <PayrollHistoryTable personId={personId} />
      </section>

      {payrollDialogOpen && <PayrollUploadDialog onClose={() => setPayrollDialogOpen(false)} />}

      {cimPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setCimPreview(null)}
        >
          <div
            className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
              <h3 className="truncate text-sm font-semibold text-gray-900">
                {employment?.cimFileName ?? t('people.employment.cimTitle')}
              </h3>
              <button
                type="button"
                onClick={() => setCimPreview(null)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label={t('common.close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <iframe src={cimPreview.url} title="CIM" className="h-full w-full flex-1" />
          </div>
        </div>
      )}
    </div>
  );
}

function TypeCard({
  icon,
  title,
  description,
  selected,
  disabled,
  onSelect,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={`flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-colors disabled:cursor-not-allowed ${
        selected
          ? 'border-brand-500 bg-brand-50'
          : 'border-gray-200 bg-white hover:border-brand-300 disabled:hover:border-gray-200'
      }`}
    >
      <span className={`mt-0.5 ${selected ? 'text-brand-600' : 'text-gray-400'}`}>{icon}</span>
      <span>
        <span className="block text-sm font-semibold text-gray-900">{title}</span>
        <span className="block text-xs text-gray-500">{description}</span>
      </span>
    </button>
  );
}

function PayrollHistoryTable({ personId }: { personId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const { data: entries, isLoading, isError } = usePersonPayroll(personId);
  const [openingDoc, setOpeningDoc] = useState<string | null>(null);

  async function openSourcePdf(entry: PayrollEntry) {
    setOpeningDoc(entry.id);
    try {
      const res = await getPayrollDocumentUrl(entry.documentId);
      window.open(res.url, '_blank', 'noopener');
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : t('people.payroll.sourceFailed'));
    } finally {
      setOpeningDoc(null);
    }
  }

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('people.payroll.loading')}
      </p>
    );
  }
  if (isError) {
    return <p className="text-sm text-red-600">{t('people.payroll.loadError')}</p>;
  }
  if (!entries || entries.length === 0) {
    return <p className="text-sm text-gray-400">{t('people.payroll.empty')}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <th className="py-2 pr-3">{t('people.payroll.month')}</th>
            <th className="py-2 pr-3 text-right">{t('people.payroll.gross')}</th>
            <th className="py-2 pr-3 text-right">{t('people.payroll.net')}</th>
            <th className="py-2 pr-3 text-right">{t('people.payroll.employeeContributions')}</th>
            <th className="py-2 pr-3 text-right">{t('people.payroll.incomeTax')}</th>
            <th className="py-2 pr-3 text-right">{t('people.payroll.cam')}</th>
            <th className="py-2 pr-3 text-right">{t('people.payroll.totalCost')}</th>
            <th className="py-2 text-right">{t('people.payroll.source')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="border-b border-gray-100 last:border-0">
              <td className="py-2 pr-3 font-medium text-gray-800">
                {String(entry.month).padStart(2, '0')}/{entry.year}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-700">
                {formatMoney(entry.grossSalary)}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-700">
                {formatMoney(entry.netSalary)}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-700">
                {formatMoney(entry.employeeContributions)}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-700">
                {formatMoney(entry.incomeTax)}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-700">
                {formatMoney(entry.employerCam)}
              </td>
              <td className="py-2 pr-3 text-right font-semibold tabular-nums text-gray-900">
                {formatMoney(entry.totalCost)}
              </td>
              <td className="py-2 text-right">
                <button
                  type="button"
                  onClick={() => void openSourcePdf(entry)}
                  disabled={openingDoc === entry.id}
                  title={entry.documentFileName}
                  className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
                >
                  {openingDoc === entry.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileText className="h-3.5 w-3.5" />
                  )}
                  PDF
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
